/**
 * IconPickerModal.tsx — the searchable Phosphor icon picker (#4).
 *
 * Replaces the old emoji grid. Modal with a search input + a scrollable grid
 * of curated Phosphor icons (from `src/graph/icons.ts`). Search matches both
 * the icon name and its lowercase tags (so "delivery" → Truck, "ai" → Brain).
 *
 * The node stores the Phosphor export name (e.g. `"Factory"`); ActorNode
 * renders the SVG via `lookupIcon`. Clicking a chip calls `onPick(name)`.
 */
import { useMemo, useState } from 'react';
import { MagnifyingGlass } from '@phosphor-icons/react';

import { ICONS } from '../graph/icons';
import { Modal } from './Modal';

interface IconPickerModalProps {
  current: string;
  onPick: (name: string) => void;
  onClose: () => void;
}

export function IconPickerModal({ current, onPick, onClose }: IconPickerModalProps): JSX.Element {
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return ICONS;
    return ICONS.filter((i) => i.name.toLowerCase().includes(q) || i.tags.some((t) => t.includes(q)));
  }, [query]);

  return (
    <Modal title="Pick an icon" onClose={onClose}>
      <div className="icon-picker">
        <div className="icon-picker-search">
          <MagnifyingGlass size={14} />
          <input
            type="text"
            className="text-input"
            placeholder="Search (e.g. truck, money, ai)…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
        </div>
        <div className="icon-picker-grid">
          {filtered.map(({ name, Component }) => (
            <button
              key={name}
              type="button"
              className={`icon-picker-chip${current === name ? ' sel' : ''}`}
              title={name}
              onClick={() => {
                onPick(name);
                onClose();
              }}
            >
              <Component size={22} weight="regular" />
              <span className="icon-picker-chip-name">{name}</span>
            </button>
          ))}
          {filtered.length === 0 && (
            <div className="icon-picker-empty">No icons match “{query}”.</div>
          )}
        </div>
        <div className="icon-picker-foot">{filtered.length} of {ICONS.length} icons</div>
      </div>
    </Modal>
  );
}
